import {Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import {ProductPackage} from '@app/interfaces/packages/product-package';
import {takeUntil} from 'rxjs/operators';
import {ProductPackagesService} from '@app/services/api/product-packages.service';
import {Observable, Subject} from 'rxjs';
import {ProductPackageSubscriptionTypeEnum} from '@app/enums/ProductPackageSubscriptionTypeEnum';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {AccountProductPackage} from '@app/interfaces/packages/account-product-package';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {UpgradeProductPackageDialogComponent} from '@app/dialogs/upgrade-product-package-dialog/upgrade-product-package-dialog.component';
import {CheckoutDialogComponent, CheckoutDialogData, CheckoutDialogResult} from '@app/dialogs/checkout-dialog/checkout-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent, DialogType} from '@app/shared/dialogs';
import {ProductPackageCodeEnum} from '@app/enums/ProductPackageCodeEnum';
import {NotificationService} from '@app/services/notification.service';
import {ConfirmUnsubscribeDialogComponent} from '@app/dialogs/confirm-unsubscribe-dialog/confirm-unsubscribe-dialog.component';
import {AccountConfig} from '@app/interfaces/config/account.config';
import {UserService} from '@app/services/userService/user.service';
import {UserProfile} from '@app/interfaces/entities/user-profile';
import {FinancingIntegrationTypeEnum} from '@app/enums/financing/FinancingIntegrationTypeEnum';
import {isBrowser} from '@app/util/is-browser';
import {getPricingUrl} from '@app/util/pricing-urls';

@Component({
  selector: 'app-package-card',
  templateUrl: './package-card.component.html',
  styleUrls: ['./package-card.component.scss'],
  standalone: false
})
export class PackageCardComponent implements OnInit, OnDestroy {

  @Input() trialExpired: boolean = false;
  @Input() productPackage: ProductPackage;
  @Input() subscriptionType: ProductPackageSubscriptionTypeEnum;

  public isLoading: boolean = false;

  public activePackage: AccountProductPackage;
  public readonly productPackageSubscriptionTypeEnum = ProductPackageSubscriptionTypeEnum;

  public countryCode: string;

  public AccountFeatureFlags = AccountFeaturesEnum;
  public isActive = false;
  public pricingUrl: string;

  public destroy$ = new Subject<void>();
  public daysTillEndOfSubscription: number = null;

  protected readonly FinancingIntegrationTypeEnum = FinancingIntegrationTypeEnum;
  protected readonly ProductPackageCodeEnum = ProductPackageCodeEnum;
  accountConfig$: Observable<AccountConfig>;
  userProfile$: Observable<UserProfile>;
  isSpecialTrialOffer: boolean;

  constructor(private productPackagesService: ProductPackagesService,
              private configApi: ConfigAPIService,
              private notificationService: NotificationService,
              private userService: UserService,
              private matDialog: MatDialog,
              @Inject(LOCALE_ID) private locale: string) {
    this.pricingUrl = getPricingUrl(this.locale);
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.userProfile$ = this.userService.getUserProfile().pipe(takeUntil(this.destroy$));

    this.accountConfig$ = this.configApi.getAccountConfig();

    this.accountConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.daysTillEndOfSubscription = data.daysTillEndOfSubscription;
        this.activePackage = data.activeAccountProductPackage;

        if (this.activePackage.productPackageUuid === this.productPackage.uuid) {
          this.isActive = true;
        }

        this.setIsSpecialTrialOffer();
        this.isLoading = false;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  get isCancelled(): boolean {
    if (!this.activePackage) {
      return false;
    }
    // A paid subscription is cancelled if it has a 'to' date in the future (and is not a trial)
    return !this.activePackage.isTrial &&
           !!this.activePackage.to &&
           new Date(this.activePackage.to) > new Date();
  }

  upgradePackage(productPackage: ProductPackage, productType: string) {
    // Trial users get a simple confirmation dialog — no payment flow
    if (this.activePackage.isTrial && !this.activePackage.isExpired) {
      const dialogRef = UpgradeProductPackageDialogComponent.open(this.matDialog, productPackage, productType);
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (isBrowser()) {
            window.location.reload();
          }
        }
      });
      return;
    }

    const dialogRef = CheckoutDialogComponent.open(this.matDialog, {
      type: 'product_package',
      productPackage,
      productPackageType: productType
    } as CheckoutDialogData);

    dialogRef.afterClosed().subscribe((result: CheckoutDialogResult) => {
      if (result?.success && result?.activated) {
        // Reload to reflect the new package
        if (isBrowser()) {
          window.location.reload();
        }
      }
    });
  }

  unsubscribePackage() {
    const dialogRef = ConfirmUnsubscribeDialogComponent.open(this.matDialog, this.productPackage);

    dialogRef.afterClosed().subscribe((feedback) => {
      if (feedback) {
        this.productPackagesService.unsubscribe(feedback).subscribe({
          next: () => {
            this.notificationService.showSuccessAlert('Your subscription will be cancelled at the end of your billing period');
            if (isBrowser()) {
              window.location.reload();
            }
          },
          error: (error) => {
            this.notificationService.showErrorAlert(error.error?.message || 'Failed to cancel subscription');
          }
        });
      }
    });
  }

  reSubscribePackage() {
    const dialogRef = ConfirmDialogComponent.open(this.matDialog, DialogType.RESUBSCRIBE, false);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.productPackagesService.reSubscribe().subscribe({
          next: () => {
            this.notificationService.showSuccessAlert('Your subscription has been reactivated');
            if (isBrowser()) {
              window.location.reload();
            }
          },
          error: (error) => {
            this.notificationService.showErrorAlert(error.error?.message || 'Failed to reactivate subscription');
          }
        });
      }
    });
  }


  public openSubscriptionPaymentSettings() {
    window.open(this.activePackage.paymentUrl, '_blank');
  }

  setIsSpecialTrialOffer() {
    const cutoffDate = new Date('2025-08-07');
    const packageFromDate = this.activePackage?.from ? new Date(this.activePackage.from) : null;

    this.isSpecialTrialOffer = this.activePackage?.isTrial === false
      && this.activePackage?.productPackage?.productCode === ProductPackageCodeEnum.SUBSCRIPTION_MINI
      && packageFromDate !== null
      && packageFromDate < cutoffDate;
  }

}
